import { useEffect, useState } from "react";
import type { VideoPageBuilderDto } from "@/types/api.types";

type State = {
  data: VideoPageBuilderDto | null;
  error: string | null;
  isLoading: boolean;
};

declare global {
  interface Window {
    postId?: number | string;
    apiUrl?: string;
    cta_link?: string;
    host?: string;
    ccRestAccess?: {
      csrfToken?: string;
    };
    generalSettings?: Record<string, unknown>;
    videoPageBuilderDto?: VideoPageBuilderDto;
  }
}

function readInlineDto(): VideoPageBuilderDto | null {
  const dto = window.videoPageBuilderDto;
  return dto && typeof dto === "object" ? dto : null;
}

function buildEndpoint(): string {
  const base = window.apiUrl ?? "/wp-json/";
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;

  return `${normalizedBase}api/v2/video-page-builder-dto/${window.postId}`;
}

export function useVideoPageBuilderData(): State {
  const [state, setState] = useState<State>(() => {
    const inlineDto = readInlineDto();
    return {
      data: inlineDto,
      error: null,
      isLoading: inlineDto === null,
    };
  });

  useEffect(() => {
    if (readInlineDto() !== null) {
      return;
    }

    const controller = new AbortController();

    async function load() {
      if (!window.postId) {
        setState({
          data: null,
          error: "Missing video page id.",
          isLoading: false,
        });
        return;
      }

      try {
        const response = await fetch(buildEndpoint(), {
          signal: controller.signal,
          credentials: "same-origin",
          headers: {
            "X-CC-CSRF-Token": window.ccRestAccess?.csrfToken ?? "",
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = (await response.json()) as VideoPageBuilderDto;
        setState({ data, error: null, isLoading: false });
      } catch (error) {
        if (controller.signal.aborted) return;

        setState({
          data: null,
          error:
            error instanceof Error
              ? error.message
              : "Unable to load video page.",
          isLoading: false,
        });
      }
    }

    void load();

    return () => controller.abort();
  }, []);

  return state;
}
